import { Grid } from '@material-ui/core';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from '../../../../../../_metronic/i18n/customUseIntl';
import RowTextFieldWithMetrics from '../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics';
import { UserProfilePrefs } from '../../../../../modules/Helpers/UserProfilePrefs';
import {
  LayingHensIntervention,
  LayingHensOutput,
} from '../../../../models/Intervention';
import { unitLong } from '../../../../utils/unit-utils';
import { ButtonControlGroup } from '../../../helpers/ButtonControlGroup';
import { DialogContainer } from '../../CommonDataParts/DialogContainer';
import { FormType } from '../../common';
import { LayingHensInterventionFormDialogProps } from '../CommonDataParts/InterventionTypes';

const LHInterventionOutputFormDialog = ({
  formType = FormType.Add,
  stageIndex = 0,
  formVisible,
  handleCancel,
  baseline,
}: LayingHensInterventionFormDialogProps) => {

  const intl = useIntl();

  // TODO Fix this after coverting UserProfilePrefs to TS
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const userProfile = UserProfilePrefs.getInstance();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
  const userUOM = userProfile.getUserUnitPrefs();
  const outputMassPrecision =
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    userProfile.getUnitOutputMassPrecision() as number;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const unitOutputMass = userUOM?.unitOutputMass
    ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      unitLong(userUOM.unitOutputMass as string)
    : 'kg';

  const currResetValue = useRef<LayingHensOutput>();

  const formTitle = intl.formatMessage({
    id: 'SUSTELL.PROCESS.DIALOG.STAGE.OUTPUT.TITLE',
  });
  const fieldItemPrefix = `stages.${stageIndex}.stageData.output`;
  const fc = useFormContext();

  useEffect(() => {
    if (formVisible) {
      currResetValue.current = {
        ...(fc.getValues(fieldItemPrefix) as LayingHensOutput),
      }; // must 'spread' otherwise just reference
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formVisible]);

  const handleResetClick = () => {
    if (currResetValue.current) {
      const resetObject = { ...fc.getValues() } as LayingHensIntervention;
      const stageData = resetObject?.stages?.[stageIndex]?.stageData;
      if (stageData?.output) {
        stageData.output = {
          ...currResetValue.current,
        };
        fc.reset(resetObject, {
          errors: true,
        });
      }
    }
    handleCancel();
  };

  const output = baseline?.stages?.[stageIndex]?.stageData?.output;

  return (
    <DialogContainer
      formVisible={formVisible}
      handleClose={handleResetClick}
      iconCode="LAYING_HENS"
      formTitle={formTitle}
      datasetType="intervention"
    >
      <Grid container direction="column" spacing={2} justifyContent="space-between">
        <Grid item xs={12}>
          <RowTextFieldWithMetrics
            name={`${fieldItemPrefix}.eggs`}
            label={intl.formatMessage({ id: 'BASELINE.FORM.OUTPUT.HENS.EGGS' })}
            type="number"
            margin="none"
            tooltip={
              <div>
                {intl.formatMessage(
                  { id: 'INTERVENTION.FORM.OUTPUT.HENS.EGGS.TOOLTIP' },
                  { br: <br /> }
                )}
              </div>
            }
            metricUnit={unitOutputMass}
            precision={outputMassPrecision}
            baseline={output?.eggs}
            disabled={formType === 'view'}
          />
        </Grid>
        <Grid item xs={12}>
          <RowTextFieldWithMetrics
            name={`${fieldItemPrefix}.liveWeight`}
            label={intl.formatMessage({
              id: 'BASELINE.FORM.OUTPUT.HENS.LIVEWEIGHT',
            })}
            type="number"
            margin="none"
            tooltip={
              <div>
                {intl.formatMessage(
                  { id: 'INTERVENTION.FORM.OUTPUT.HENS.LIVEWEIGHT.TOOLTIP' },
                  { br: <br /> }
                )}
              </div>
            }
            metricUnit={unitOutputMass}
            precision={outputMassPrecision}
            baseline={output?.liveWeight}
            disabled={formType === 'view'}
          />
        </Grid>
        <Grid item xs={11}>
          <ButtonControlGroup
            cancelHandler={handleResetClick}
            saveHandler={handleCancel}
            saveLabel={intl.formatMessage({ id: 'GENERAL.CONFIRM' })}
          />
        </Grid>
      </Grid>
    </DialogContainer>
  );
};

export default LHInterventionOutputFormDialog;
